import { useRequestErrorToast } from '@onefootprint/hooks';
import request from '@onefootprint/request';
import type { Organization } from '@onefootprint/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuthHeaders } from '@/hooks/use-session';
import useSession from '@/hooks/use-session';

export type PostAssumeRequest = {
  tenantId: string;
};

export type PostAssumeResponse = {
  token: string;
  tenant: Organization;
};

export const useSwitchTenant = () => {
  const {
    logIn,
    data: { org },
    setIsLive,
  } = useSession();
  const assumeMutation = useAssumeTenant();
  const queryClient = useQueryClient();

  // Returns didSwitch so callers don't recompute it from their own (possibly stale) `org`.
  // `isLive` omitted means the object doesn't belong to one mode, so keep the current one.
  return async ({ tenantId, isLive }: { tenantId: string; isLive?: boolean }): Promise<{ didSwitch: boolean }> => {
    const isSameTenant = tenantId === org?.id;

    if (isSameTenant) {
      if (isLive !== undefined && isLive !== org?.isLive) {
        await setIsLive(isLive);
        queryClient.invalidateQueries();
      }
      return { didSwitch: false };
    }
    const { token } = await assumeMutation.mutateAsync({ tenantId });
    await logIn({ auth: token, newIsLive: isLive });
    queryClient.invalidateQueries();
    return { didSwitch: true };
  };
};

const postAssumeTenantReadOnly = async (authHeaders: AuthHeaders, tenantId: string) => {
  const { data } = await request<PostAssumeResponse>({
    method: 'POST',
    url: '/private/assume',
    headers: authHeaders,
    data: {
      tenantId,
    },
  });
  return data;
};

const useAssumeTenant = () => {
  const { authHeaders } = useSession();
  const showErrorToast = useRequestErrorToast();

  return useMutation({
    mutationFn: (data: PostAssumeRequest) => postAssumeTenantReadOnly(authHeaders, data.tenantId),
    onError: (error: unknown) => {
      showErrorToast(error);
    },
  });
};

export default useAssumeTenant;
